import { Transition } from '@headlessui/react';
import { useState } from 'react';
import { UserAvatar } from 'src/components';
import { useCurrentUser, useUpdateUserRole } from 'src/hooks';
import TrashIcon from 'src/images/icons/trash-icon.svg?react';
import type { MembershipRole, OrgMember } from 'src/types';
import { RoleDropdown } from '.';

type Props = {
  orgUser: OrgMember;
  handleClickRemoveUser: (user: OrgMember) => void;
};

export const UserDetails = ({ orgUser, handleClickRemoveUser }: Props) => {
  const { user } = useCurrentUser();
  const [userRole, setUserRole] = useState<MembershipRole>(orgUser.role);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteIcon, setShowDeleteIcon] = useState(false);

  const { mutate: updateUserRole } = useUpdateUserRole();

  const userIsPending = orgUser.status === 'pending';

  // TODO - close dropdown after clicking
  // TODO - add loading spinner next to dropdown
  const handleSelectRole = (role: MembershipRole, id: string) => {
    setUserRole(role);

    updateUserRole({
      id: orgUser.id,
      email: orgUser.email,
      role,
      status: orgUser.status,
    });

    setShowDropdown(false);
  };

  const shouldShowDeleteIcon = () => {
    if (user?.subscription.role === 'admin' && user.email !== orgUser.email) {
      return true;
    }

    return false;
  };

  return (
    <li
      className='flex min-w-full flex-row items-center justify-start gap-6 border-b border-blueGray px-6 py-2 first:border-t-0 sm:px-8 md:px-10 lg:px-12'
      onMouseEnter={() => shouldShowDeleteIcon() && setShowDeleteIcon(true)}
      onMouseLeave={() => setShowDeleteIcon(false)}
    >
      <div
        className={`${userIsPending ? 'opacity-70' : ''} flex w-[350px] min-w-[350px] flex-row flex-nowrap items-center justify-start gap-3`}
      >
        {/* User Avatar */}
        <div className='flex h-8 w-8 cursor-pointer items-center justify-center transition-all hover:opacity-90'>
          <UserAvatar
            name={orgUser.name || orgUser.email.toUpperCase()}
            photo={orgUser.photo}
          />
        </div>

        {/* User Name */}
        <span className='heading-100 w-full truncate whitespace-nowrap text-primary-dark'>
          {orgUser.name}
        </span>
      </div>

      {/* User Email */}
      <div
        className={`${userIsPending ? 'opacity-40' : ''} flex w-[350px] min-w-[350px] items-center justify-start`}
      >
        <span className='text-100 truncate whitespace-nowrap text-primary-dark'>
          {orgUser.email}
        </span>
      </div>

      {/* User Role / Status */}
      <RoleDropdown
        showDropdown={showDropdown}
        handleShowDropdown={() => setShowDropdown(!showDropdown)}
        isPending={userIsPending}
        displayPending={userIsPending}
        role={userRole}
        handleSelectRole={handleSelectRole}
        dropdownId={`user-role-dropdown-${orgUser.id}`}
        userId={orgUser.id}
        disableDropdown={
          user?.subscription.role !== 'admin' || orgUser.email === user?.email
        }
      />

      {/* Delete User Button */}
      <div className='ml-auto w-[18px] min-w-[18px]'>
        <Transition
          as={'div'}
          show={showDeleteIcon}
          enter='transition ease-out duration-150'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <TrashIcon
            className={`stroke-primary transition hover:cursor-pointer hover:stroke-primary/70`}
            onClick={() => handleClickRemoveUser(orgUser)}
          />
        </Transition>
      </div>
    </li>
  );
};
